import React, { useCallback, useEffect, useMemo, useState } from 'react';
import RTModal from '../common/RTModal';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { apiGet, apiPost } from '../../api/apiClient';
import {
  suggestUsernameFromName,
  validateManagementUsername,
  USERNAME_RULES_MESSAGE,
} from '../../utils/authHelpers';
import { toastError, toastSuccess } from '../common/toast';
import './AddUserModal.css';

const PERM = 'access-control.manage_users';

const emptyForm = () => ({
  staff_id: '',
  username: '',
  mobile: '',
  role_id: '',
});

const AddUserModal = ({ isOpen, onClose, onCreated }) => {
  const [mode, setMode] = useState('staff');
  const [form, setForm] = useState(emptyForm);
  const [staffOptions, setStaffOptions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;

    setMode('staff');
    setForm(emptyForm());
    setErrors({});
    setLoadingOptions(true);

    const load = async () => {
      try {
        const [staffRes, rolesRes] = await Promise.all([
          apiGet('/auth-users/staff-options'),
          apiGet('/auth-users/assignable-roles'),
        ]);
        const staffList = staffRes.data?.staff || [];
        const roleList = rolesRes.data?.roles || [];
        setStaffOptions(staffList);
        setRoles(roleList);
        setForm((prev) => ({
          ...prev,
          role_id: roleList.length ? String(roleList[0].id) : '',
        }));
      } catch (err) {
        toastError(err?.response?.data?.detail || 'Failed to load add-user options');
      } finally {
        setLoadingOptions(false);
      }
    };

    load();
  }, [isOpen]);

  const clearFieldError = (key) => {
    setErrors((prev) => {
      if (!prev[key] && !prev.submit) return prev;
      const next = { ...prev };
      delete next[key];
      delete next.submit;
      return next;
    });
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    clearFieldError(key);
  };

  const onStaffSelect = (staffId) => {
    const staff = staffOptions.find((s) => String(s.staff_id) === String(staffId));
    setForm((prev) => ({
      ...prev,
      staff_id: staffId,
      username: suggestUsernameFromName(staff?.name || ''),
      mobile: staff?.mobile || '',
    }));
    clearFieldError('staff_id');
    clearFieldError('username');
    clearFieldError('mobile');
  };

  const setModeAndReset = (nextMode) => {
    setMode(nextMode);
    setForm((prev) => ({
      ...emptyForm(),
      role_id: prev.role_id,
    }));
    setErrors({});
  };

  const validateForm = () => {
    const next = {};
    if (mode === 'staff' && !form.staff_id) {
      next.staff_id = 'Select a staff member';
    }
    const usernameError = validateManagementUsername(form.username);
    if (usernameError) {
      next.username = usernameError;
    }
    if (form.mobile.trim()) {
      const digits = form.mobile.replace(/\D/g, '');
      if (digits.length !== 10) {
        next.mobile = 'Mobile must be a valid 10-digit number';
      }
    }
    if (!form.role_id) {
      next.role_id = 'Role is required';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const checkUsernameAvailable = async (username) => {
    const res = await apiGet(
      `/auth-users/check-username?username=${encodeURIComponent(username)}`
    );
    return Boolean(res.data?.available);
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.submit;
      return next;
    });

    try {
      const username = form.username.trim().toLowerCase();
      const available = await checkUsernameAvailable(username);
      if (!available) {
        setErrors({ username: 'Username is already taken' });
        setSaving(false);
        return;
      }

      const payload = {
        username,
        role_id: Number(form.role_id),
        mobile: form.mobile.trim() || null,
        staff_id: mode === 'staff' ? Number(form.staff_id) : null,
      };
      const res = await apiPost('/auth-users', payload);
      toastSuccess(res.data?.message || 'User created');
      onCreated?.();
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to create user';
      const text = typeof msg === 'string' ? msg : 'Failed to create user';
      setErrors({ submit: text });
      toastError(text);
    } finally {
      setSaving(false);
    }
  };

  return (
    <RTModal
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      title="Add Management User"
      size="medium"
      disableBackdropClick={saving}
      footer={(
        <>
          <RTButton
            variant="outline-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </RTButton>
          <RTButton
            permissionKey={PERM}
            variant="primary"
            onClick={handleSave}
            disabled={saving || loadingOptions}
          >
            {saving ? 'Saving…' : 'Save'}
          </RTButton>
        </>
      )}
    >
      <div className="add-user-modal">
        {(saving || loadingOptions) && (
          <div className="add-user-modal-overlay">
            <RTSpinner
              size={50}
              message={saving ? 'Saving user…' : 'Loading…'}
            />
          </div>
        )}

        <div className="add-user-mode-row">
          <label className="add-user-radio">
            <input
              type="radio"
              name="add-user-mode"
              checked={mode === 'staff'}
              onChange={() => setModeAndReset('staff')}
              disabled={saving}
            />
            Existing staff
          </label>
          <label className="add-user-radio">
            <input
              type="radio"
              name="add-user-mode"
              checked={mode === 'temporary'}
              onChange={() => setModeAndReset('temporary')}
              disabled={saving}
            />
            Temporary / non-staff
          </label>
        </div>

        {mode === 'staff' ? (
          <div className="add-user-field">
            <label htmlFor="add-user-staff">Staff *</label>
            <select
              id="add-user-staff"
              className={errors.staff_id ? 'error' : ''}
              value={form.staff_id}
              onChange={(e) => onStaffSelect(e.target.value)}
              disabled={saving || loadingOptions}
            >
              <option value="">Select staff without login…</option>
              {staffOptions.map((s) => (
                <option key={s.staff_id} value={s.staff_id}>
                  {s.name} ({s.mobile})
                </option>
              ))}
            </select>
            {errors.staff_id ? (
              <span className="error-message">{errors.staff_id}</span>
            ) : null}
          </div>
        ) : null}

        <div className="add-user-field">
          <label htmlFor="add-user-username">Username *</label>
          <input
            id="add-user-username"
            className={errors.username ? 'error' : ''}
            value={form.username}
            onChange={(e) => handleChange('username', e.target.value.toLowerCase())}
            maxLength={64}
            disabled={saving}
            placeholder="e.g. new_staff"
          />
          {errors.username ? (
            <span className="error-message">{errors.username}</span>
          ) : (
            <span className="add-user-hint">{USERNAME_RULES_MESSAGE}</span>
          )}
        </div>

        <div className="add-user-field">
          <label htmlFor="add-user-mobile">Mobile</label>
          <input
            id="add-user-mobile"
            className={errors.mobile ? 'error' : ''}
            value={form.mobile}
            onChange={(e) => handleChange('mobile', e.target.value)}
            placeholder="Optional 10-digit mobile"
            maxLength={15}
            disabled={saving}
          />
          {errors.mobile ? (
            <span className="error-message">{errors.mobile}</span>
          ) : null}
        </div>

        <div className="add-user-field">
          <label htmlFor="add-user-role">Role *</label>
          <select
            id="add-user-role"
            className={errors.role_id ? 'error' : ''}
            value={form.role_id}
            onChange={(e) => handleChange('role_id', e.target.value)}
            disabled={saving || loadingOptions}
          >
            {roles.length === 0 ? (
              <option value="">No assignable roles</option>
            ) : (
              roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))
            )}
          </select>
          {errors.role_id ? (
            <span className="error-message">{errors.role_id}</span>
          ) : null}
        </div>

        {errors.submit ? (
          <div className="error-message submit-error">{errors.submit}</div>
        ) : null}
      </div>
    </RTModal>
  );
};

export default AddUserModal;
